import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconUpload, IconAdjustments, IconPlugConnected } from '@tabler/icons-react'
import { Button } from '@/components/buttons/Button'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'

import { Card, CardGrid } from '@/components/cards/Card'
import { getHierarchyOrganizationOptions, getOrganizationOptions } from '@/utils/helpers'
import { fetchPlaybooksByOrg } from '@/reducers/analytics/analytics.actions'
import { ManageKnowledgeBaseButton } from '@/views/Organizations/components/buttons/ManageKnowledgeBaseButton'

import { UploadKnowledge, ManageKnowledge } from './AiAnswersKnowledgeActions'
import { UploadBaltoKnowledge, ManageBaltoKnowledge } from './BaltoKnowledgeActions'
import { findTargetOrganization } from './AiAnswers.helpers'
import './AiAnswers.scss'

const LANDING_VIEW = 'landing'

// Which knowledge base the Upload/Manage views act on. Balto admins can switch
// to the shared base; everyone else only ever sees their own organization's.
const ORGANIZATION_SCOPE = 'organization'
const CENTRAL_SCOPE = 'central'

const KNOWLEDGE_VIEWS = {
  upload: {
    key: 'upload',
    title: 'Upload',
    description: 'Add documents to be used by AgentGPT',
    iconClassName: 'ai-answers-upload',
    icon: IconUpload,
    Component: UploadKnowledge,
    CentralComponent: UploadBaltoKnowledge,
  },
  manage: {
    key: 'manage',
    title: 'Manage',
    description: 'Manage documents powering AgentGPT',
    iconClassName: 'ai-answers-search-or-delete',
    icon: IconAdjustments,
    Component: ManageKnowledge,
    CentralComponent: ManageBaltoKnowledge,
  },
}

/**
 * @param {number} props.organizationId the org the Connect card manages
 */
export const AiAnswersKnowledge = ({ organizationId }) => {
  const dispatch = useDispatch()

  const currentUser = useSelector((state) => state.currentUser)
  const {
    organizationid: currentUserOrgId,
    token,
    username,
    hierarchy_manager: isHierarchyManager,
  } = currentUser

  const { organizations, currentOrganization } = useSelector((state) => state)
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const { data, loading } = useSelector((state) => state.analytics)

  const isBaltoAdmin = currentUserOrgId === 1
  const canSelectOrganization = isBaltoAdmin || isHierarchyManager
  const organizationOptions = isBaltoAdmin
    ? getOrganizationOptions(organizations)
    : canSelectOrganization
      ? getHierarchyOrganizationOptions(flattenedUserOrgHierarchy)
      : []

  const [activeView, setActiveView] = useState(LANDING_VIEW)
  const [scope, setScope] = useState(ORGANIZATION_SCOPE)
  const isCentralScope = isBaltoAdmin && scope === CENTRAL_SCOPE
  const targetOrganization = findTargetOrganization({
    organizationId,
    currentUserOrgId,
    currentOrganization,
    organizations,
  })
  // Connections only make sense for an org that can use AgentGPT; the central base has none.
  const canConnect = !isCentralScope && !!targetOrganization?.agent_gpt_enabled

  useEffect(() => {
    if (!canSelectOrganization) {
      dispatch(fetchPlaybooksByOrg(currentUserOrgId))
    }
  }, [dispatch, canSelectOrganization, currentUserOrgId])

  const sharedProps = {
    canSelectOrganization,
    currentUserOrgId,
    token,
    username,
    organizationOptions,
    playbooks: data?.playbooks || [],
    playbooksLoading: loading?.playbooks,
    onOrganizationSelect: (orgId) => dispatch(fetchPlaybooksByOrg(orgId)),
  }

  const knowledgeViews = Object.values(KNOWLEDGE_VIEWS)
  const activeViewConfig = KNOWLEDGE_VIEWS[activeView]
  // In central scope the organization and playbook selectors are meaningless, so
  // the views are swapped wholesale rather than conditionally emptied.
  const ActiveComponent = activeViewConfig
    ? isCentralScope
      ? activeViewConfig.CentralComponent
      : activeViewConfig.Component
    : null

  const handleScopeChange = (nextScope) => {
    setScope(nextScope)
    setActiveView(LANDING_VIEW)
  }

  return (
    <div className="flex-column gap" data-testid="ai-answers-knowledge">
      {isBaltoAdmin && (
        <SegmentedControl
          data-testid="ai-answers-knowledge-scope"
          items={[
            {
              accessor: ORGANIZATION_SCOPE,
              label: 'Organization',
              active: !isCentralScope,
              onClick: () => handleScopeChange(ORGANIZATION_SCOPE),
              dataTestId: 'ai-answers-scope-organization',
            },
            {
              accessor: CENTRAL_SCOPE,
              label: 'Central knowledge base',
              active: isCentralScope,
              onClick: () => handleScopeChange(CENTRAL_SCOPE),
              dataTestId: 'ai-answers-scope-central',
              tooltip: 'Balto documentation shared across every organization',
            },
          ]}
        />
      )}

      {activeView === LANDING_VIEW && (
        <CardGrid data-testid="ai-answers-knowledge-landing">
          {knowledgeViews.map(({ key, title, description, iconClassName, icon: IconComponent }) => (
            <Card
              key={key}
              title={title}
              icon={
                <div className={`interaction-icon-container ${iconClassName}`}>
                  <IconComponent size={24} />
                </div>
              }
              data-testid={`ai-answers-landing-card-${key}`}
            >
              <p className="muted-text">{description}</p>
              <Button
                data-testid={`ai-answers-landing-cta-${key}`}
                onClick={() => setActiveView(key)}
              >
                Get Started
              </Button>
            </Card>
          ))}
          {canConnect && (
            <Card
              title="Connect"
              icon={
                <div className="interaction-icon-container ai-answers-connect">
                  <IconPlugConnected size={24} />
                </div>
              }
              data-testid="ai-answers-landing-card-connect"
            >
              <p className="muted-text">
                {isBaltoAdmin
                  ? `Sync documents from SharePoint or Google Drive for ${targetOrganization.name}`
                  : 'Sync documents from SharePoint or Google Drive'}
              </p>
              <ManageKnowledgeBaseButton
                organizationId={organizationId}
                organizationName={targetOrganization.name}
                buttonLabel="Get Started"
                buttonProps={{ 'data-testid': 'ai-answers-landing-cta-connect' }}
              />
            </Card>
          )}
        </CardGrid>
      )}

      {/* The central base has no organization or playbook dimension at all, and
          its requests authenticate through fetchingAPI, so those views take
          nothing but the back handler. */}
      {ActiveComponent ? (
        <ActiveComponent
          {...(isCentralScope ? {} : sharedProps)}
          onBack={() => setActiveView(LANDING_VIEW)}
        />
      ) : null}
    </div>
  )
}

export default AiAnswersKnowledge
